import { useEffect, useRef, useState, RefObject } from 'react'
import { createPortal } from 'react-dom'
import TouchableOpacity from '../../TouchableOpacity'
import Text from '../../Text'
import styles from './style'

export interface InstanceLogViewHandle {
  toggle: (instanceName: string) => void
}

interface LogWindowState {
  instanceName: string
  visible: boolean
}

interface InstanceLogViewModalProps {
  logsByInstance: Record<string, string[]>
  onSubscribe: (instanceName: string) => void
  onUnsubscribe: (instanceName: string) => void
  ref?: RefObject<InstanceLogViewHandle | null>
}

function InstanceLogViewModal({
  logsByInstance,
  onSubscribe,
  onUnsubscribe,
  ref,
}: InstanceLogViewModalProps) {
  const [windows, setWindows] = useState<LogWindowState[]>([])

  useEffect(() => {
    if (!ref) return

    ref.current = {
      toggle: instanceName => {
        setWindows(prevState => {
          const existing = prevState.find(w => w.instanceName === instanceName)
          if (existing) {
            return prevState.map(w =>
              w.instanceName === instanceName ? { ...w, visible: !w.visible } : w
            )
          }

          onSubscribe(instanceName)
          return [...prevState, { instanceName, visible: true }]
        })
      },
    }
  }, [ref, onSubscribe])

  function toggleMinimize(instanceName: string) {
    setWindows(prevState =>
      prevState.map(w => (w.instanceName === instanceName ? { ...w, visible: !w.visible } : w))
    )
  }

  function closeWindow(instanceName: string) {
    onUnsubscribe(instanceName)
    setWindows(prevState => prevState.filter(w => w.instanceName !== instanceName))
  }

  if (windows.length === 0) return null

  const visibleWindows = windows.filter(w => w.visible)

  return createPortal(
    <div style={styles.overlay}>
      {visibleWindows.length > 0 ? (
        <div style={styles.windowRow}>
          {visibleWindows.map(w => (
            <LogWindow
              key={w.instanceName}
              instanceName={w.instanceName}
              messages={logsByInstance[w.instanceName] ?? []}
              onMinimize={() => toggleMinimize(w.instanceName)}
              onClose={() => closeWindow(w.instanceName)}
            />
          ))}
        </div>
      ) : null}

      <div style={styles.taskbar}>
        {windows.map(w => (
          <div
            key={w.instanceName}
            style={styles.taskbarItem}
          >
            <TouchableOpacity onClick={() => toggleMinimize(w.instanceName)}>
              <Text style={w.visible ? styles.taskbarLabelActive : styles.taskbarLabel}>
                {w.instanceName}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onClick={() => closeWindow(w.instanceName)}>
              <Text style={styles.taskbarClose}>✕</Text>
            </TouchableOpacity>
          </div>
        ))}
      </div>
    </div>,
    document.body
  )
}

interface LogWindowProps {
  instanceName: string
  messages: string[]
  onMinimize: () => void
  onClose: () => void
}

// Distance (px) from the bottom within which the log is still considered
// "at the bottom", so new lines keep auto-scrolling despite rounding.
const stickToBottomThreshold = 24

// Cosmetic copy of the backend's error patterns (instanceManager.ts, via
// @expostreamer/shared-types/ffmpegErrorPatterns) used only to highlight
// lines here — not the authoritative error/crash flag. Not imported from
// the shared package directly: CRA's webpack config only runs TS/babel
// transforms over react/src, so it can't parse a raw .ts file pulled in
// (even transitively) from a workspace package outside src/.
const FFMPEG_ERROR_PATTERNS = [
  /no such file or directory/i,
  /invalid data found when processing input/i,
  /connection refused/i,
  /connection timed out/i,
  /server returned 4\d\d/i,
  /server returned 5\d\d/i,
  /permission denied/i,
  /could not find codec parameters/i,
  /error while (opening|decoding|demuxing|muxing)/i,
  /unable to find a suitable output format/i,
]

function isErrorLine(line: string): boolean {
  return FFMPEG_ERROR_PATTERNS.some(pattern => pattern.test(line))
}

function LogWindow({ instanceName, messages, onMinimize, onClose }: LogWindowProps) {
  const logRef = useRef<HTMLDivElement>(null)
  const stickToBottomRef = useRef(true)

  function handleScroll() {
    const el = logRef.current
    if (!el) return
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < stickToBottomThreshold
  }

  useEffect(() => {
    const el = logRef.current
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight
  }, [messages.length])

  return (
    <div style={styles.window}>
      <div style={styles.windowHeader}>
        <TouchableOpacity
          style={styles.windowHeaderLabel}
          onClick={onMinimize}
        >
          <Text style={styles.windowTitle}>{instanceName}</Text>
        </TouchableOpacity>
        <TouchableOpacity onClick={onClose}>
          <Text style={styles.closeIcon}>✕</Text>
        </TouchableOpacity>
      </div>

      <div
        ref={logRef}
        style={styles.log}
        onScroll={handleScroll}
      >
        {messages.length === 0 ? (
          <Text style={styles.emptyText}>Warte auf Ausgabe...</Text>
        ) : (
          messages.map((line, index) => (
            <Text
              key={index}
              style={isErrorLine(line) ? styles.logLineError : styles.logLine}
            >
              {line}
            </Text>
          ))
        )}
      </div>
    </div>
  )
}

export default InstanceLogViewModal
